import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import {
  RecaptchaV3Module,
  RECAPTCHA_BASE_URL,
  RECAPTCHA_V3_SITE_KEY,
} from 'ng-recaptcha-2';
import { ToastrModule } from 'ngx-toastr';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgxTranslateCutModule } from 'ngx-translate-cut';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MatSelectSearchModule } from './components/mat-select-search/mat-select-search.module';
import { SharedComponentModule } from './components/shared-components.module';
import { AccountTypeData } from './data-repositories/account-type-data';
import { BusinessDataService } from './data-repositories/business-data.service';
import { CountryService } from './data-repositories/country-service';
import { CurrencyData } from './data-repositories/currency-data';
import { CustomerTypeData } from './data-repositories/customer-type-data';
import { DateData } from './data-repositories/date-data';
import { DependentsData } from './data-repositories/dependents-data';
import { EmploymentStatusData } from './data-repositories/employment-status-data';
import { ExplanationData } from './data-repositories/explanation-data';
import { FeaturesService } from './data-repositories/features.service';
import { GenderData } from './data-repositories/gender-data';
import { IdentificationTypeData } from './data-repositories/identification-type-data';
import { KnowledgeLevelData } from './data-repositories/knowledge-level-data';
import { LanguagesData } from './data-repositories/languages-data';
import { LoginDataService } from './data-repositories/login-data.service';
import { MaritalData } from './data-repositories/marital-data';
import { MoneyRangeData } from './data-repositories/money-range-data';
import { NatureOfBusinessData } from './data-repositories/nature-of-business-data';
import { PhoneCodeData } from './data-repositories/phone-code-data';
import { PhoneTypeData } from './data-repositories/phone-type-data';
import { SalutationTypeData } from './data-repositories/salutation-type-data';
import { SecurityQuestionData } from './data-repositories/security-question-data';
import { SourceOfWealthData } from './data-repositories/source-of-wealth-data';
import { TradesPerYearData } from './data-repositories/trades-per-year-data';
import { YearsTradingDataList } from './data-repositories/years-trading-data';
import { ConfirmationComponent } from './pages/confirmation/confirmation.component';
import { EditUserComponent } from './pages/edit-user/edit-user.component';
import { InviteComponent } from './pages/invite/invite.component';
import { LoginByPhoneComponent } from './pages/login-by-phone/login-by-phone.component';
import { LoginComponent } from './pages/login/login.component';
import { RequestPasswordComponent } from './pages/request-password/request-password.component';
import { TermsAndConditionsComponent } from './pages/terms-and-conditions/terms-and-conditions.component';
import { WsModule } from './pages/workspace/ws/ws.module';
import { AuthInterceptorService } from './repositories/auth-interceptor.service';
import { AuthorizeGuard } from './repositories/authorize-guard.service';
import { ConfirmationGuardService } from './repositories/confirmation-guard.service';
import { NotAuhorizedGuardService } from './repositories/not-auhorized-guard.service';
import { BrowserStorageService } from './services/browser-storage.service';
import { DbTranslateLoader } from './services/db-translate.loader';
import { MissingTranslationHandlerLogger } from './services/missing-translation-logger.handler';
import { ThankYouComponent } from './pages/thank-you/thank-you.component';
import { ThankYouWithoutDocumentsComponent } from './pages/thank-you-without-documents/thank-you-without-documents.component';
import { PreLoginComponent } from './pages/login/pre-login/pre-login.component';
import { CodeInputComponent } from './pages/confirmation/code-input/code-input.component';
import { MatCheckboxModule } from '@angular/material/checkbox';

export function createTranslateLoader(
  http: HttpClient,
  storageService: BrowserStorageService
) {
  return new DbTranslateLoader(http, storageService);
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ConfirmationComponent,
    EditUserComponent,
    TermsAndConditionsComponent,
    RequestPasswordComponent,
    InviteComponent,
    LoginByPhoneComponent,
    ThankYouComponent,
    ThankYouWithoutDocumentsComponent,
    PreLoginComponent,
    CodeInputComponent
  ],
  imports: [
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient, BrowserStorageService],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationHandlerLogger,
      },
      useDefaultLang: false,
    }),
    NgxTranslateCutModule.forRoot(),
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatMomentDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSelectSearchModule,
    MatButtonModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    ToastrModule.forRoot(),
    WsModule,
    BsDatepickerModule,
    SharedComponentModule,
    RecaptchaV3Module,
    MatRadioModule,
    MatCheckboxModule
  ],
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    AuthorizeGuard,
    NotAuhorizedGuardService,
    ConfirmationGuardService,
    LoginDataService,
    BusinessDataService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    CountryService,
    {
      provide: APP_INITIALIZER,
      useFactory: countryServiceFactory,
      deps: [CountryService],
      multi: true,
    },
    FeaturesService,
    {
      provide: APP_INITIALIZER,
      useFactory: featuresServiceFactory,
      deps: [FeaturesService],
      multi: true,
    },
    GenderData,
    DependentsData,
    MaritalData,
    PhoneTypeData,
    EmploymentStatusData,
    NatureOfBusinessData,
    AccountTypeData,
    SecurityQuestionData,
    MoneyRangeData,
    CurrencyData,
    CustomerTypeData,
    IdentificationTypeData,
    YearsTradingDataList,
    TradesPerYearData,
    KnowledgeLevelData,
    SalutationTypeData,
    ExplanationData,
    LanguagesData,
    SourceOfWealthData,
    PhoneCodeData,
    DateData,
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { hideSingleSelectionIndicator: true },
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.Recaptcha.SiteKey,
    },
    {
      provide: RECAPTCHA_BASE_URL,
      useValue: environment.Recaptcha.BaseUrl,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}

export function countryServiceFactory(provider: CountryService) {
  return () => provider.getCountryNameByIP();
}

export function featuresServiceFactory(provider: FeaturesService) {
  return () => provider.loadFeatures();
}
